// frontend/src/pages/MyBetsPage.tsx
import React, { useState, useEffect } from 'react';
import { Bet, UserInfo, Draw } from '../types'; // Import Bet, UserInfo, and Draw types
import { toast } from 'react-toastify'; // Import toast for notifications

// Define an interface for the error response structure from your backend
interface ErrorResponse {
    message: string;
    // Add other potential error properties if your backend sends them, e.g.:
    // statusCode?: number;
    // errors?: string[];
}

const MyBetsPage: React.FC = () => {
    const [myBets, setMyBets] = useState<Bet[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const userInfoString = localStorage.getItem('userInfo');
    const user: UserInfo | null = userInfoString ? JSON.parse(userInfoString) : null;

    // Custom Tailwind animations via CSS (copied for consistency)
    const customAnimations = `
        @keyframes fade-in-down {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoom-in-out {
            0% { transform: scale(0.9); opacity: 0; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
        }
        @keyframes bounce-subtle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
        }
        .animate-fade-in-down {
            animation: fade-in-down 0.8s ease-out forwards;
        }
        .animate-fade-in-up {
            animation: fade-in-up 0.8s ease-out forwards;
        }
        .animate-zoom-in-out {
            animation: zoom-in-out 0.5s ease-out forwards;
        }
        .animate-bounce-subtle {
            animation: bounce-subtle 1.5s infinite ease-in-out;
        }
    `;

    useEffect(() => {
        const fetchMyBets = async () => {
            if (!user || !user.token) {
                setError('You must be logged in to view your bets.');
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bets/my-bets`, {
                    headers: {
                        'Authorization': `Bearer ${user.token}`,
                    },
                });

                const data: any = await res.json();

                if (!res.ok) {
                    const errorData = data as ErrorResponse;
                    throw new Error(errorData.message || 'Failed to fetch your bets due to an unknown error.');
                }

                setMyBets(data as Bet[]);

            } catch (err: any) {
                setError((err instanceof Error ? err.message : String(err)) || 'An unexpected error occurred while fetching your bets.');
                toast.error((err instanceof Error ? err.message : String(err)) || 'Error fetching bets.');
            } finally {
                setLoading(false);
            }
        };

        fetchMyBets();
    }, [user]);

    if (loading) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-200">
                <style>{customAnimations}</style> {/* Inject animations for loading state */}
                <div className="text-center">
                    <p className="text-2xl font-semibold text-purple-700 animate-pulse-slow">
                        Fetching your lucky bets...
                    </p>
                    <div className="mt-4 w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4 md:p-8 min-h-screen flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100">
                <style>{customAnimations}</style> {/* Inject animations for error state */}
                <div className="bg-white p-8 rounded-lg shadow-xl border-t-8 border-red-500 w-full max-w-md text-center animate-zoom-in-out">
                    <h2 className="text-3xl font-bold text-red-700 mb-4">Oops! Something went wrong.</h2>
                    <p className="text-lg text-gray-700">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-6 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg transition duration-300 shadow-md"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 md:p-8 mt-8 font-inter bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
            <style>{customAnimations}</style> {/* Inject custom animations */}

            <h1 className="text-4xl font-extrabold text-center mb-10 text-indigo-800 animate-fade-in-down">
                ✨ Your Betting History ✨
            </h1>

            {myBets.length === 0 ? (
                <div className="bg-white p-8 rounded-xl shadow-lg border-t-8 border-yellow-400 text-center max-w-md mx-auto animate-zoom-in-out">
                    <p className="text-2xl text-gray-700 mb-4">
                        Looks like you haven't placed any bets yet! 😢
                    </p>
                    <p className="text-lg text-gray-600 mb-6">
                        The thrill of the win awaits you.
                    </p>
                    <a href="/" className="inline-block bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 transform hover:scale-105">
                        Start Betting Now!
                    </a>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {myBets.map(bet => {
                        const isDrawPopulated = (bet.draw && typeof bet.draw !== 'string');
                        const drawDetails: Draw | undefined = isDrawPopulated ? (bet.draw as Draw) : undefined;

                        const betStatusClass =
                            bet.status === 'pending' ? 'bg-yellow-500' :
                            bet.status === 'confirmed' ? 'bg-green-500' :
                            bet.status === 'expired' ? 'bg-red-500' : 'bg-gray-500';

                        const outcomeStatusClass =
                            bet.isWinner ? 'text-green-700 bg-green-100 border-green-400' :
                            drawDetails?.status === 'declared' && !bet.isWinner ? 'text-red-700 bg-red-100 border-red-400' : '';

                        return (
                            <div key={bet._id} className="bg-white p-6 rounded-xl shadow-xl border-t-4 border-indigo-300 transform transition-transform duration-300 hover:scale-[1.02] hover:shadow-2xl animate-fade-in-up">
                                <div className={`absolute top-0 right-0 m-4 px-3 py-1 rounded-full text-white text-xs font-bold uppercase ${betStatusClass} shadow-md`}>
                                    {bet.status}
                                </div>
                                <h2 className="text-2xl font-bold text-indigo-700 mb-3 pt-2">
                                    Draw: {drawDetails ? drawDetails.name : 'N/A'}
                                </h2>

                                {/* Displaying more Draw Details if populated */}
                                {isDrawPopulated && drawDetails && (
                                    <>
                                        <p className="text-gray-700 text-sm mb-1">
                                            <span className="font-semibold text-gray-800">Deadline:</span> {new Date(drawDetails.deadline).toLocaleString()}
                                        </p>
                                        <p className="text-gray-700 text-sm mb-1">
                                            <span className="font-semibold text-gray-800">Entry Cost:</span> Rs. {drawDetails.entryCost.toFixed(2)}
                                        </p>
                                        <p className="text-gray-700 text-sm mb-1">
                                            <span className="font-semibold text-gray-800">Prize Pool:</span> {drawDetails.prizePoolType === 'percentage' ? `${drawDetails.prizeValue}% (Growing)` : `Rs. ${drawDetails.prizeValue.toFixed(2)}`}
                                        </p>
                                    </>
                                )}

                                {/* Existing Bet Details */}
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <p className="text-lg text-gray-700 mb-1">
                                        <span className="font-bold text-blue-700">Your Numbers:</span>{' '}
                                        <span className="font-extrabold text-purple-700">{bet.selectedNumbers.join(', ')}</span>
                                    </p>
                                    <p className="text-lg text-gray-700 mb-1">
                                        <span className="font-bold text-blue-700">Bet Amount:</span>{' '}
                                        <span className="font-extrabold text-green-700">Rs. {bet.amountPaid.toFixed(2)}</span>
                                    </p>
                                    {bet.paymentMethod && (
                                        <p className="text-gray-700 text-sm mb-1">
                                            <span className="font-medium">Method:</span> {bet.paymentMethod}
                                        </p>
                                    )}
                                    {bet.referenceNumber && (
                                        <p className="text-gray-700 text-sm mb-1 break-words">
                                            <span className="font-medium">Reference:</span> {bet.referenceNumber}
                                        </p>
                                    )}
                                </div>


                                {/* Display Draw Status and Winner/Loser status */}
                                {isDrawPopulated && drawDetails?.status === 'declared' && typeof drawDetails.winningNumber === 'number' && (
                                    <div className={`mt-4 p-3 rounded-lg border text-center ${outcomeStatusClass} animate-bounce-subtle`}>
                                        <p className="text-md font-bold mb-1">
                                            Winning Number: <span className="text-2xl font-extrabold">{drawDetails.winningNumber < 10 ? `0${drawDetails.winningNumber}` : drawDetails.winningNumber}</span>
                                        </p>
                                        <p className="text-xl font-extrabold">
                                            {bet.isWinner ? '🎉 YOU ARE A WINNER! 🎉' : 'Better Luck Next Time!'}
                                        </p>
                                        {bet.isWinner && bet.payoutStatus && (
                                            <p className="text-md mt-2">
                                                <span className="font-semibold">Payout Status:</span>{' '}
                                                <span className={`font-bold uppercase ${bet.payoutStatus === 'paid' ? 'text-green-800' : 'text-orange-800'}`}>
                                                    {bet.payoutStatus.replace(/_/g, ' ')}
                                                </span>
                                            </p>
                                        )}
                                        {bet.isWinner && (
                                            <p className="text-md">
                                                <span className="font-semibold">Payout Amount:</span>{' '}
                                                {/* MODIFIED LINE HERE */}
                                                <span className="font-extrabold">Rs. {(bet.payoutAmount ?? 0).toFixed(2)}</span>
                                            </p>
                                        )}
                                    </div>
                                )}
                                {/* IMPORTANT: Ensure drawDetails is checked before accessing its properties */}
                                {isDrawPopulated && drawDetails && drawDetails.status !== 'declared' && (
                                     <p className="text-gray-700 text-md mt-4 text-center p-2 bg-blue-50 rounded-md border border-blue-200">
                                         <span className="font-medium text-blue-700">Draw Status:</span>{' '}
                                         <span className="text-blue-600 font-semibold capitalize">{drawDetails.status}</span>
                                     </p>
                                )}
                                <p className="text-sm text-gray-500 mt-4 text-right">
                                    Placed On: {new Date(bet.createdAt).toLocaleString()}
                                </p>
                                {bet.updatedAt && (
                                    <p className="text-sm text-gray-500 text-right">
                                        Last Updated: {new Date(bet.updatedAt).toLocaleString()}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyBetsPage;